import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/layouts/ClientLayout';
import { FileText, Download, TrendingUp, Eye, MessageSquare, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';

const ClientReportsPage = () => {
  const { api } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await api.get('/reports');
      setReports(response.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const kpiIcons = {
    reach: Eye,
    engagement: MessageSquare,
    leads: Users,
    views: TrendingUp,
    watch_time: TrendingUp,
  };

  return (
    <ClientLayout>
      <div className="space-y-6" data-testid="client-reports">
        <div>
          <h1 className="text-2xl font-syne font-bold text-white">Performance Reports</h1>
          <p className="text-fp-textMuted mt-1">Monthly performance insights and analytics</p>
        </div>

        {reports.length === 0 ? (
          <div className="bg-fp-surface border border-white/5 rounded-sm p-12 text-center">
            <FileText className="w-12 h-12 text-fp-textMuted mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">No reports yet</h3>
            <p className="text-fp-textMuted">Your monthly reports will appear here once available.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {reports.map((report) => (
              <div
                key={report.id}
                className="bg-fp-surface border border-white/5 rounded-sm overflow-hidden"
                data-testid={`report-card-${report.id}`}
              >
                {/* Header */}
                <div className="p-6 border-b border-white/5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center">
                        <FileText className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h2 className="text-xl font-syne font-bold text-white">{report.month}</h2>
                        <p className="text-fp-textMuted text-sm">
                          Generated: {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {report.pdf_url && (
                      <a
                        href={report.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary flex items-center gap-2"
                        data-testid={`download-report-${report.id}`}
                      >
                        <Download className="w-4 h-4" />
                        Download PDF
                      </a>
                    )}
                  </div>
                </div>

                {/* KPIs */}
                <div className="p-6">
                  <h3 className="text-fp-textMuted text-sm mb-4">Key Performance Indicators</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {Object.entries(report.kpis || {}).map(([key, value]) => {
                      const Icon = kpiIcons[key] || TrendingUp;
                      return (
                        <div
                          key={key}
                          className="p-4 bg-fp-bg rounded-sm text-center"
                        >
                          <Icon className="w-5 h-5 text-primary mx-auto mb-2" />
                          <p className="text-2xl font-syne font-bold text-white">
                            {formatNumber(value)}
                          </p>
                          <p className="text-fp-textMuted text-xs capitalize mt-1">
                            {key.replace('_', ' ')}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Help Section */}
        <div className="bg-primary/10 border border-primary/20 rounded-sm p-6">
          <h3 className="font-semibold text-white mb-2">Need More Details?</h3>
          <p className="text-fp-textMuted text-sm mb-4">
            Have questions about your report or want a deeper analysis? Let's discuss.
          </p>
          <a
            href="https://wa.me/917030771071"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="report-contact-btn"
          >
            <MessageSquare className="w-4 h-4" />
            Chat with Us
          </a>
        </div>
      </div>
    </ClientLayout>
  );
};

export default ClientReportsPage;
